import { create } from 'zustand';
import type { KpiBlock } from '../types/remiBlocks';
import {
  getDashboardPreferences,
  saveDashboardPreferences,
  getDashboardBlocks,
  createDashboardBlock,
  deleteDashboardBlock,
} from '../api/remiApi';

const STORAGE_KEY = 'dashboard-prefs';
const orgStorageKey = (orgId: string) => `dashboard-prefs:${orgId}`;
const LEGACY_WIDGET_KEY = 'dashboard-widget-prefs';
const LEGACY_PINNED_KEY = 'remi-pinned-widgets';

const FLYWHEEL_DEFAULT_ORDER = ['flywheel', 'handoffs', 'live_transfers'];
const NON_FLYWHEEL_DEFAULT_ORDER = ['calls', 'leads', 'live_transfers'];

/** Widgets that are always locked regardless of flywheel. */
export const LOCKED_WIDGETS = ['live_transfers'];

/** Returns locked widget IDs based on flywheel status. */
export const getLockedWidgets = (flywheelEnabled: boolean): string[] =>
  flywheelEnabled ? ['flywheel', 'handoffs', 'live_transfers'] : ['live_transfers'];

/** Returns the default widget order based on flywheel status. */
export const getDefaultOrder = (flywheelEnabled: boolean): string[] =>
  flywheelEnabled ? FLYWHEEL_DEFAULT_ORDER : NON_FLYWHEEL_DEFAULT_ORDER;

export const KNOWN_WIDGET_IDS = [
  'flywheel',
  'leads',
  'calls',
  'handoffs',
  'live_transfers',
  'opportunities',
  'email',
  'website',
  'page_views',
  'active_users',
  'remi_touches',
  'nurture',
  'search_alerts',
  'sms',
  'appointments',
];

export type WidgetSize = 1 | 2;

// ── Pinned widgets ─────────────────────────────────────────────────────────

export interface PinnedWidget {
  id: string;
  pinnedAt: string;
  block: KpiBlock;
}

/**
 * Stable deduplication key derived from data_ref (entity + id + sorted fields).
 * Falls back to lowercased title when no data_ref is present.
 * Same metric from any conversation always yields the same key.
 */
export function dataRefKey(block: KpiBlock): string {
  const ref = block.data_ref;
  if (!ref) return `title:${block.title.toLowerCase().trim()}`;
  const fields = ref.fields?.length ? [...ref.fields].sort().join(',') : '';
  return `${ref.entity}:${ref.id}${fields ? ':' + fields : ''}`;
}

/**
 * Returns the deterministic widget store ID for a pinned KPI block.
 * Always produces the same ID for the same data_ref — no random suffixes.
 * Example: "pinned-organization_global_total_contacts"
 */
export function getPinnedWidgetId(block: KpiBlock): string {
  return `pinned-${dataRefKey(block).replace(/[^a-z0-9]/gi, '_')}`;
}

// ── State ──────────────────────────────────────────────────────────────────

interface WidgetPreferencesState {
  widgetOrder: string[];
  widgetSizes: Record<string, WidgetSize>;
  widgetHighlights: Record<string, boolean>;
  isCustomized: boolean;
  pinnedWidgets: PinnedWidget[];
  isSyncing: boolean;

  setWidgetOrder: (order: string[]) => void;
  addWidget: (id: string) => void;
  removeWidget: (id: string) => void;
  getWidgetSize: (id: string) => WidgetSize;
  setWidgetSize: (id: string, size: WidgetSize) => void;
  isHighlighted: (id: string) => boolean;
  toggleHighlight: (id: string) => void;
  resetToDefaults: () => void;

  /** Pin a Remi KPI widget — calls POST /dashboard/blocks, adds blockId to widgetOrder. */
  pinWidget: (block: KpiBlock) => Promise<void>;
  /** Remove a pinned widget — calls DELETE /dashboard/blocks/{id}, removes from widgetOrder. */
  unpinWidget: (id: string) => Promise<void>;
  /** True if a widget with the same data_ref (or title) is already pinned. */
  isPinned: (block: KpiBlock) => boolean;
  /** Load preferences + blocks from Remi API. Call once on mount. */
  syncFromAPI: (organizationId: string) => Promise<void>;
}

interface StorageData {
  widgetOrder: string[];
  widgetSizes: Record<string, WidgetSize>;
  widgetHighlights: Record<string, boolean>;
  isCustomized: boolean;
  pinnedWidgets: PinnedWidget[];
}

// ── Storage ────────────────────────────────────────────────────────────────

const BLANK: StorageData = {
  widgetOrder: FLYWHEEL_DEFAULT_ORDER,
  widgetSizes: {},
  widgetHighlights: {},
  isCustomized: false,
  pinnedWidgets: [],
};

function normalizeOrder(order: string[]): string[] {
  const o = order
    .map((id) => (id === 'opportunities' ? 'calls' : id))
    .filter((id) => id !== 'liveTransfers' && id !== 'touches');
  if (!o.includes('calls') && !o.includes('handoffs')) o.push('calls');
  for (const locked of LOCKED_WIDGETS) {
    if (!o.includes(locked)) o.unshift(locked);
  }
  return o;
}

const loadFromStorage = (): StorageData => {
  try {
    // ── Unified key (current format) ──────────────────────────────────────
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        widgetOrder: normalizeOrder(parsed.widgetOrder || FLYWHEEL_DEFAULT_ORDER),
        widgetSizes: parsed.widgetSizes || {},
        widgetHighlights: parsed.widgetHighlights || {},
        isCustomized: parsed.isCustomized || false,
        pinnedWidgets: parsed.pinnedWidgets || [],
      };
    }

    // ── Migrate from legacy separate keys ─────────────────────────────────
    let order: string[] = FLYWHEEL_DEFAULT_ORDER;
    let widgetSizes: Record<string, WidgetSize> = {};
    let widgetHighlights: Record<string, boolean> = {};
    let isCustomized = false;
    let pinnedWidgets: PinnedWidget[] = [];

    const legacyWidget = localStorage.getItem(LEGACY_WIDGET_KEY);
    if (legacyWidget) {
      const p = JSON.parse(legacyWidget);
      order = normalizeOrder(p.widgetOrder?.length ? p.widgetOrder : FLYWHEEL_DEFAULT_ORDER);
      widgetSizes = p.widgetSizes || {};
      widgetHighlights = p.widgetHighlights || {};
      isCustomized = p.isCustomized || false;
    }

    const legacyPinned = localStorage.getItem(LEGACY_PINNED_KEY);
    if (legacyPinned) {
      const legacy = JSON.parse(legacyPinned) as Array<{
        id: string;
        pinnedAt: string;
        block: KpiBlock;
      }>;

      // Deduplicate by dataRefKey, assign deterministic stable IDs
      const seen = new Set<string>();
      const idMap = new Map<string, string>(); // old random id → new stable id
      pinnedWidgets = legacy
        .filter((pw) => {
          const key = dataRefKey(pw.block);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((pw) => {
          const newId = getPinnedWidgetId(pw.block);
          idMap.set(pw.id, newId);
          return { id: newId, pinnedAt: pw.pinnedAt, block: pw.block };
        });

      // Remap old random IDs in widgetOrder to new stable IDs
      order = order.map((id) => idMap.get(id) ?? id);
    }

    if (legacyWidget || legacyPinned) {
      return { widgetOrder: order, widgetSizes, widgetHighlights, isCustomized, pinnedWidgets };
    }
  } catch {
    // ignore parse errors
  }
  return BLANK;
};

// Current org ID — set by syncFromAPI so saveToStorage can include it
let currentOrgId: string | null = null;

const saveToStorage = (state: StorageData) => {
  try {
    const key = currentOrgId ? orgStorageKey(currentOrgId) : STORAGE_KEY;
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // ignore write errors
  }
  // Fire-and-forget API sync (only if org is known)
  if (currentOrgId) {
    saveDashboardPreferences(currentOrgId, state as unknown as Record<string, unknown>).catch(() => {
      // ignore — localStorage is the fallback
    });
  }
};

// ── Store ──────────────────────────────────────────────────────────────────

export const useWidgetPreferences = create<WidgetPreferencesState>((set, get) => {
  const initial = loadFromStorage();

  const save = () => {
    const s = get();
    saveToStorage({
      widgetOrder: s.widgetOrder,
      widgetSizes: s.widgetSizes,
      widgetHighlights: s.widgetHighlights,
      isCustomized: s.isCustomized,
      pinnedWidgets: s.pinnedWidgets,
    });
  };

  return {
    widgetOrder: initial.widgetOrder,
    widgetSizes: initial.widgetSizes,
    widgetHighlights: initial.widgetHighlights,
    isCustomized: initial.isCustomized,
    pinnedWidgets: initial.pinnedWidgets,
    isSyncing: false,

    setWidgetOrder: (order) => {
      set({ widgetOrder: order, isCustomized: true });
      save();
    },

    addWidget: (id) => {
      const current = get().widgetOrder;
      if (current.includes(id)) return;
      set({ widgetOrder: [...current, id], isCustomized: true });
      save();
    },

    removeWidget: (id) => {
      if (LOCKED_WIDGETS.includes(id)) return;
      const sizes = { ...get().widgetSizes };
      const highlights = { ...get().widgetHighlights };
      delete sizes[id];
      delete highlights[id];
      set({
        widgetOrder: get().widgetOrder.filter((w) => w !== id),
        widgetSizes: sizes,
        widgetHighlights: highlights,
        isCustomized: true,
      });
      save();
    },

    getWidgetSize: (id) => get().widgetSizes[id] || 1,

    setWidgetSize: (id, size) => {
      set({ widgetSizes: { ...get().widgetSizes, [id]: size } });
      save();
    },

    isHighlighted: (id) => !!get().widgetHighlights[id],

    toggleHighlight: (id) => {
      const current = get().widgetHighlights;
      set({ widgetHighlights: { ...current, [id]: !current[id] } });
      save();
    },

    resetToDefaults: () => {
      set({
        widgetOrder: FLYWHEEL_DEFAULT_ORDER,
        widgetSizes: {},
        widgetHighlights: {},
        pinnedWidgets: [],
        isCustomized: false,
      });
      save();
    },

    pinWidget: async (block) => {
      if (!currentOrgId) return;
      const key = dataRefKey(block);
      if (get().pinnedWidgets.some((w) => dataRefKey(w.block) === key)) return;
      set({ isSyncing: true });
      try {
        const raw = await createDashboardBlock(currentOrgId, {
          type: block.type,
          title: block.title,
          payload: {
            value: block.value,
            delta: block.delta,
            unit: block.unit,
            trend: block.trend,
            change_pct: block.change_pct,
            timeframe: block.timeframe,
            source: block.source,
          },
          dataRef: block.data_ref,
        });
        // API returns { success: true, block: { blockId, ... } }
        const blockData = (raw.block || raw) as Record<string, unknown>;
        const blockId = (blockData.blockId as string) || (blockData.block_id as string);
        if (!blockId) throw new Error('No blockId returned from blocks API');
        const pw: PinnedWidget = { id: blockId, pinnedAt: new Date().toISOString(), block };
        set({ pinnedWidgets: [...get().pinnedWidgets, pw] });
        // Add to widgetOrder and save preferences
        const current = get().widgetOrder;
        if (!current.includes(blockId)) {
          set({ widgetOrder: [...current, blockId], isCustomized: true });
          save();
        }
        set({ isSyncing: false });
      } catch (err) {
        console.warn('[dashboard-prefs] pinWidget failed', err);
        set({ isSyncing: false });
      }
    },

    unpinWidget: async (id) => {
      set({
        pinnedWidgets: get().pinnedWidgets.filter((w) => w.id !== id),
        widgetOrder: get().widgetOrder.filter((wid) => wid !== id),
        isCustomized: true,
      });
      save();
      try {
        await deleteDashboardBlock(id);
      } catch (err) {
        console.warn('[dashboard-prefs] deleteDashboardBlock failed', err);
      }
    },

    isPinned: (block) => {
      const key = dataRefKey(block);
      return get().pinnedWidgets.some((w) => dataRefKey(w.block) === key);
    },

    syncFromAPI: async (organizationId: string) => {
      currentOrgId = organizationId;
      // Pre-load org-scoped localStorage so there's no flash on org switch
      try {
        const orgRaw = localStorage.getItem(orgStorageKey(organizationId));
        if (orgRaw) {
          const p = JSON.parse(orgRaw);
          set({
            widgetOrder: normalizeOrder(p.widgetOrder?.length ? p.widgetOrder : FLYWHEEL_DEFAULT_ORDER),
            widgetSizes: p.widgetSizes || {},
            widgetHighlights: p.widgetHighlights || {},
            isCustomized: p.isCustomized || false,
            pinnedWidgets: p.pinnedWidgets || [],
          });
        }
      } catch { /* ignore */ }
      set({ isSyncing: true });
      try {

        const [data, rawBlocks] = await Promise.all([
          getDashboardPreferences(organizationId).catch(() => null),
          getDashboardBlocks(organizationId).catch(() => [] as Array<Record<string, unknown>>),
        ]);

        // Convert /blocks rows to PinnedWidget shape
        const pinnedFromBlocks: PinnedWidget[] = (rawBlocks || []).map((sb) => {
          const payload = (sb.payload || {}) as Record<string, unknown>;
          const dr = (sb.dataRef || sb.data_ref) as Record<string, unknown> | undefined;
          const block: KpiBlock = {
            type: 'kpi',
            id: sb.blockId as string,
            title: sb.title as string,
            value: (payload.value as number | string) ?? 0,
            delta: (payload.delta as string | null) ?? null,
            unit: (payload.unit as string | null) ?? null,
            trend: (payload.trend as string | null) ?? null,
            change_pct: (payload.change_pct as number | null) ?? null,
            timeframe: (payload.timeframe as string | null) ?? null,
            source: (payload.source as string | null) ?? null,
            data_ref: dr
              ? {
                  entity: dr.entity as string,
                  id: dr.id as string,
                  fields: dr.fields as string[] | undefined,
                }
              : undefined,
          };
          return { id: sb.blockId as string, pinnedAt: sb.createdAt as string, block };
        });

        if (!data) {
          // No saved prefs for this org — reset to defaults
          set({
            widgetOrder: normalizeOrder(FLYWHEEL_DEFAULT_ORDER),
            widgetSizes: {},
            widgetHighlights: {},
            isCustomized: false,
            pinnedWidgets: pinnedFromBlocks,
            isSyncing: false,
          });
          return;
        }

        // API returns { success: true, preferences: { widgetOrder, ... } } — unwrap the wrapper
        const prefs = (data.preferences as Record<string, unknown>) ?? data;

        // Some deployments embed pinned widgets directly in /preferences; prefer
        // those when present (already in PinnedWidget shape) and fall back to /blocks.
        const pinnedFromPrefs = Array.isArray(prefs.pinnedWidgets)
          ? (prefs.pinnedWidgets as PinnedWidget[]).filter(
              (pw) => pw && typeof pw.id === 'string' && pw.block && typeof pw.block.title === 'string',
            )
          : [];
        const pinnedWidgets: PinnedWidget[] =
          pinnedFromPrefs.length > 0 ? pinnedFromPrefs : pinnedFromBlocks;

        const parsed: StorageData = {
          widgetOrder: normalizeOrder((prefs.widgetOrder as string[])?.length ? (prefs.widgetOrder as string[]) : FLYWHEEL_DEFAULT_ORDER),
          widgetSizes: (prefs.widgetSizes as Record<string, WidgetSize>) || {},
          widgetHighlights: (prefs.widgetHighlights as Record<string, boolean>) || {},
          isCustomized: (prefs.isCustomized as boolean) || false,
          pinnedWidgets,
        };

        set({ ...parsed, isSyncing: false });
        try {
          localStorage.setItem(orgStorageKey(organizationId), JSON.stringify({ ...parsed, pinnedWidgets: [] }));
        } catch {
          /* ignore */
        }
      } catch (err) {
        console.warn('[dashboard-prefs] syncFromAPI failed — keeping local state', err);
        set({ isSyncing: false });
      }
    },
  };
});
